import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Calendar as CalendarIcon, Lock, Repeat, Users, MoreHorizontal, Maximize2, Minimize2, XCircle, Home, MapPin, FileText, Building2, Pencil, Copy } from 'lucide-react';
import { readableDate } from './createShiftServiceRules';   // UA-4 polish
import { getStatusStyle } from './calendarUtils';
import { ShiftPeriodBadgeForShift } from './ShiftPeriodBadge';
import { statusLabel, windowLabel, lockedBanner } from './shiftSheetRules';

/** UI-02 / UI-08 / UI-13 / UI-22 — sticky header of the Shift Details sheet. Display + header menu only. */
export function ShiftSheetHeader({ shift, group, locked, agreement, expanded, onToggleExpand, onCancel, canCancel, onOpenGroup, onRequestCorrection, canCorrect,
  onEdit, editState = { can: false, reason: '' }, onDuplicate, duplicateState = { can: false, reason: '' } }) {
  const style = getStatusStyle(shift.status);
  const banner = lockedBanner(locked);
  const location = shift.house_name || shift.session_location || shift.location || shift.location_name || '';
  return (
    <div className="sticky top-0 z-10 bg-background border-b -mx-6 px-6 pt-4 pb-3 space-y-2" data-testid="shift-sheet-header">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-base font-semibold truncate" data-testid="sheet-participant-name">{shift.participant_name || shift.display_name || 'Shift'}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap tabular-nums">
            <CalendarIcon className="w-3.5 h-3.5" /> <span data-testid="sheet-header-date" title={shift.date}>{readableDate(shift.date)}</span>
            <span className="font-medium text-foreground" data-testid="sheet-window">{windowLabel(shift)}</span>
            {location && <span className="inline-flex items-center gap-1" data-testid="sheet-location">{shift.house_name ? <Home className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}{location}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* BS3-001: edit date, time, participant, service, ratio and workers of this shift */}
          {onEdit && (
            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={onEdit} disabled={!editState.can}
              title={!editState.can ? editState.reason : editState.silTimeOnly ? 'Change the date and time of this SIL house shift' : 'Edit this shift'} data-testid="sheet-edit-shift">
              <Pencil className="w-3.5 h-3.5 mr-1" /> {editState.silTimeOnly ? 'Edit date / time' : 'Edit'}
            </Button>
          )}
          {/* BS3-002: copy this shift to another date (staff not copied) */}
          {onDuplicate && (
            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={onDuplicate} disabled={!duplicateState.can}
              title={duplicateState.can ? 'Copy this shift to another date' : duplicateState.reason} data-testid="sheet-duplicate-shift">
              <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hidden sm:inline-flex" onClick={onToggleExpand} title={expanded ? 'Dock to the side' : 'Expand to full page'} data-testid="sheet-expand-toggle">
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid="sheet-menu"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!canCancel} onSelect={onCancel} className="text-red-600" data-testid="sheet-cancel-shift">
                <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel shift (reason required)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge className={`${style.bg} ${style.text} border`} data-testid="sheet-status">{statusLabel(shift.status)}</Badge>
        <ShiftPeriodBadgeForShift shift={shift} size="sm" />
        {shift.check_in_time && !shift.check_out_time && <Badge variant="outline" className="text-sky-600 border-sky-300 text-[10px]">Worker on shift</Badge>}
        {locked && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700" data-testid="sheet-lock-state"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>}
        {shift.recurring && (
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600" title="Edits here change this occurrence only — the series is not rewritten" data-testid="sheet-recurring">
            <Repeat className="w-3 h-3 mr-1" /> Recurring · this occurrence only
          </Badge>
        )}
        {(group || shift.shared_group_id) && (
          <button type="button" onClick={onOpenGroup} className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] text-sky-800 hover:bg-sky-100" data-testid="sheet-group-chip">
            <Users className="w-3 h-3" /> {group?.delivery_type === 'SIL_HOUSE' ? 'SIL house group' : 'Shared delivery group'}{group ? ` · ${(group.members || []).length}` : ''}
          </button>
        )}
        {agreement && (
          <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-800" title="ACTIVE service agreement authorising this shift's services" data-testid="sheet-agreement">
            <FileText className="w-3 h-3 mr-1" /> Agreement {agreement.agreement_no || ''} v{agreement.version_no ?? '—'}{agreement.end_date ? ` · valid to ${agreement.end_date}` : ''}
          </Badge>
        )}
        {/* #6: agreement / line straight from the shift when the authorised-services lookup has nothing (e.g. SIL/house shifts) */}
        {!agreement && shift.agreement_no && (
          <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-800" title="Service agreement this shift was generated from" data-testid="sheet-agreement-from-shift">
            <FileText className="w-3 h-3 mr-1" /> Agreement {shift.agreement_no}{shift.support_item_number ? ` · ${shift.support_item_number}` : ''}{shift.service_family_name ? ` · ${shift.service_family_name}` : ''}
          </Badge>
        )}
        {!agreement && !shift.agreement_no && shift.billing_source === 'FUNDING_PLAN' && (
          <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800" title="Rostered without a service agreement; billed from the participant's funding plan" data-testid="sheet-funding-plan">
            <FileText className="w-3 h-3 mr-1" /> No agreement · funding plan{shift.service_name ? ` · ${shift.service_name}` : ''}
          </Badge>
        )}
        {(shift.branch_name || shift.branch_id) && (
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-700" data-testid="sheet-branch">
            <Building2 className="w-3 h-3 mr-1" /> Branch {shift.branch_name || shift.branch_id}
          </Badge>
        )}
      </div>
      {banner && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid="sheet-locked-banner">
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 shrink-0" /> {banner.title} — {banner.reason}</span>
          <Button size="sm" variant="outline" className="h-6 text-[11px] border-amber-400 text-amber-900 hover:bg-amber-100" onClick={onRequestCorrection} disabled={!canCorrect} title={canCorrect ? 'Correct a delivered record with a reason (audited)' : 'Roster manage permission required'} data-testid="sheet-request-correction">
            {banner.action}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ShiftSheetHeader;
